/**
 * SatchelWalletContext
 *
 * Provides a shared wallet state across the component tree.
 * This prevents multiple instances of useSatchelWallet from creating
 * separate polling intervals and API clients.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  SatchelApiClient,
  SatchelApiException,
  type BitcoinBalance,
  type RGBBalance,
} from "../services/satchelApi";
import {
  BITCOIN_NETWORK,
  ERROR_MESSAGES,
  RGB_INFRA_POLL_INTERVAL,
  WALLET_STORAGE_KEYS,
} from "../config";
import type { BitcoinNetwork } from "../types";
import { isElectron } from "../utils/runtime";
import { getElectronAPI } from "../adapters/electron";

// Storage keys for wallet credentials (from centralized config)
const WALLET_NAME_KEY = WALLET_STORAGE_KEYS.WALLET_NAME;
const WALLET_PASSWORD_KEY = WALLET_STORAGE_KEYS.PASSWORD;
const NETWORK_KEY = WALLET_STORAGE_KEYS.NETWORK;

interface WalletState {
  walletName: string | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  bitcoinBalance: BitcoinBalance | null;
  rgbBalances: RGBBalance[];
  /** True when wallet needs password re-authentication (session expired) */
  needsPasswordAuth: boolean;
  /** Current Bitcoin network (regtest, testnet, mainnet, etc.) */
  network: BitcoinNetwork;
}

export interface SatchelWalletContextValue extends WalletState {
  setWallet: (walletName: string, password: string) => void;
  clearWallet: () => void;
  /** Forget the session password; the wallet stays on this device behind the lock screen */
  lock: () => void;
  refresh: () => Promise<void>;
  sync: () => Promise<void>;
  /** Restore wallet access using password (for returning users) */
  restoreWithPassword: (password: string) => void;
  /** Set the Bitcoin network (triggers balance refresh) */
  setNetwork: (network: BitcoinNetwork) => void;
  api: SatchelApiClient;
  /** Current wallet password (from session storage) - use with caution */
  walletPassword: string | null;
}

const SatchelWalletContext = createContext<SatchelWalletContextValue | null>(null);

export interface SatchelWalletProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that manages wallet state for the entire app.
 * Should be placed at the root of the component tree.
 */
export const SatchelWalletProvider: React.FC<SatchelWalletProviderProps> = ({ children }) => {
  const [state, setState] = useState<WalletState>({
    walletName: null,
    isConnected: false,
    isLoading: true,
    error: null,
    bitcoinBalance: null,
    rgbBalances: [],
    needsPasswordAuth: false,
    network: (localStorage.getItem(NETWORK_KEY) as BitcoinNetwork) || BITCOIN_NETWORK,
  });

  const [password, setPassword] = useState<string | null>(null);
  const isRefreshingRef = useRef(false);

  // Refs to avoid stale closure issues in callbacks
  const walletNameRef = useRef<string | null>(null);
  const passwordRef = useRef<string | null>(null);
  const networkRef = useRef<BitcoinNetwork>(state.network);

  // Keep networkRef in sync with state
  useEffect(() => {
    networkRef.current = state.network;
  }, [state.network]);

  // Single API client instance for the entire app
  const api = useMemo(() => new SatchelApiClient(), []);

  /**
   * Validate that a cached wallet still exists on the backend.
   * Clears the cache if the wallet no longer exists.
   */
  const validateWalletExists = useCallback(
    async (walletName: string, network: BitcoinNetwork): Promise<boolean> => {
      try {
        const wallets = await api.listWallets(network);
        const exists = wallets.some((w) => w.name === walletName && w.network === network);
        return exists;
      } catch {
        // If API is unavailable, don't clear cache - user might just need to start server
        return true;
      }
    },
    [api],
  );

  /**
   * Clear stale wallet cache when wallet doesn't exist on backend.
   * Also clears the onboarding completed flag so the app redirects to onboarding.
   */
  const clearStaleWalletCache = useCallback(() => {
    localStorage.removeItem(WALLET_NAME_KEY);
    sessionStorage.removeItem(WALLET_PASSWORD_KEY);
    // Clear onboarding flag so app redirects to create/import wallet flow
    localStorage.removeItem("satchel_onboarding_completed");
    walletNameRef.current = null;
    passwordRef.current = null;
    setPassword(null);
    // Clear cached credentials in Electron main process
    const electronAPI = getElectronAPI();
    if (isElectron && electronAPI?.clearWalletCredentials) {
      electronAPI.clearWalletCredentials();
    }
    setState(() => ({
      walletName: null,
      isConnected: false,
      isLoading: false,
      error: null,
      bitcoinBalance: null,
      rgbBalances: [],
      needsPasswordAuth: false,
      network: (localStorage.getItem(NETWORK_KEY) as BitcoinNetwork) || BITCOIN_NETWORK,
    }));
  }, []);

  // Load wallet name from storage on mount and validate it exists
  useEffect(() => {
    const initializeWallet = async () => {
      const storedWalletName = localStorage.getItem(WALLET_NAME_KEY);
      const storedPassword = sessionStorage.getItem(WALLET_PASSWORD_KEY);
      const storedNetwork =
        (localStorage.getItem(NETWORK_KEY) as BitcoinNetwork) || BITCOIN_NETWORK;

      if (!storedWalletName) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      // Validate wallet exists on backend before restoring from cache
      const walletExists = await validateWalletExists(storedWalletName, storedNetwork);
      if (!walletExists) {
        console.warn(
          `[SatchelWalletContext] Cached wallet "${storedWalletName}" not found on backend, clearing cache`,
        );
        clearStaleWalletCache();
        return;
      }

      // If we have a wallet name but no password, user needs to re-authenticate
      const needsPasswordAuth = Boolean(storedWalletName && !storedPassword);

      walletNameRef.current = storedWalletName;
      setState((prev) => ({
        ...prev,
        walletName: storedWalletName,
        needsPasswordAuth,
        isLoading: false,
      }));

      if (storedPassword) {
        passwordRef.current = storedPassword;
        setPassword(storedPassword);
      }

      // Re-cache credentials in Electron main process on mount (e.g., after page reload)
      const electronAPI = getElectronAPI();
      if (storedWalletName && storedPassword && isElectron && electronAPI?.cacheWalletCredentials) {
        electronAPI.cacheWalletCredentials({
          walletName: storedWalletName,
          password: storedPassword,
          network: storedNetwork,
        });
      }
    };

    initializeWallet();
  }, [validateWalletExists, clearStaleWalletCache]);

  const setWallet = useCallback((walletName: string, pwd: string) => {
    localStorage.setItem(WALLET_NAME_KEY, walletName);
    sessionStorage.setItem(WALLET_PASSWORD_KEY, pwd);
    // Update refs immediately for use in callbacks
    walletNameRef.current = walletName;
    passwordRef.current = pwd;
    setPassword(pwd);
    setState((prev) => {
      // Cache credentials in Electron main process for companion server
      const electronAPI = getElectronAPI();
      if (isElectron && electronAPI?.cacheWalletCredentials) {
        electronAPI.cacheWalletCredentials({
          walletName,
          password: pwd,
          network: prev.network,
        });
      }
      return {
        ...prev,
        walletName,
        error: null,
        needsPasswordAuth: false,
      };
    });
  }, []);

  const clearWallet = useCallback(() => {
    const forgottenWallet = walletNameRef.current;
    localStorage.removeItem(WALLET_NAME_KEY);
    sessionStorage.removeItem(WALLET_PASSWORD_KEY);
    // Clear refs immediately
    walletNameRef.current = null;
    passwordRef.current = null;
    setPassword(null);
    // Clear cached credentials in Electron main process
    const electronAPI = getElectronAPI();
    if (isElectron && electronAPI?.clearWalletCredentials) {
      electronAPI.clearWalletCredentials();
    }
    // The user is discarding this wallet on this device, so its stored
    // recovery phrase goes with it.
    if (forgottenWallet && electronAPI?.walletSeed) {
      void electronAPI.walletSeed.remove(forgottenWallet);
    }
    setState((prev) => ({
      walletName: null,
      isConnected: false,
      isLoading: false,
      error: null,
      bitcoinBalance: null,
      rgbBalances: [],
      needsPasswordAuth: false,
      network: prev.network, // Preserve network setting
    }));
  }, []);

  const lock = useCallback(() => {
    sessionStorage.removeItem(WALLET_PASSWORD_KEY);
    passwordRef.current = null;
    setPassword(null);
    const electronAPI = getElectronAPI();
    if (isElectron && electronAPI?.clearWalletCredentials) {
      electronAPI.clearWalletCredentials();
    }
    setState((prev) => ({
      ...prev,
      isConnected: false,
      isLoading: false,
      error: null,
      bitcoinBalance: null,
      rgbBalances: [],
      needsPasswordAuth: true,
    }));
  }, []);

  const refresh = useCallback(async () => {
    // Use refs to get current values (avoids stale closure)
    const currentWalletName = walletNameRef.current;
    const currentPassword = passwordRef.current;
    const currentNetwork = networkRef.current;

    if (!currentWalletName || !currentPassword) {
      setState((prev) => ({
        ...prev,
        error: ERROR_MESSAGES.NOT_SET_UP,
        isLoading: false,
      }));
      return;
    }

    // Prevent concurrent refreshes
    if (isRefreshingRef.current) {
      return;
    }
    isRefreshingRef.current = true;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const [btcBalance, rgbBalances] = await Promise.all([
        api.getBalance(currentWalletName, currentPassword, currentNetwork),
        api.getRgbBalance(currentWalletName, currentPassword, currentNetwork),
      ]);
      // Locked while the request was in flight.
      if (passwordRef.current !== currentPassword) return;

      setState((prev) => ({
        ...prev,
        isConnected: true,
        isLoading: false,
        error: null,
        bitcoinBalance: btcBalance,
        rgbBalances,
      }));
    } catch (err) {
      const rawMessage =
        err instanceof SatchelApiException ? err.message : err instanceof Error ? err.message : "";

      // Detect decryption / wrong-password errors from the backend
      // These contain patterns like "Decryption failed", "aead::Error", "wrong password"
      const isDecryptionError =
        rawMessage.toLowerCase().includes("decryption") ||
        rawMessage.toLowerCase().includes("aead") ||
        rawMessage.toLowerCase().includes("wrong password");

      if (isDecryptionError) {
        // Clear the bad password and send user back to the lock screen
        sessionStorage.removeItem(WALLET_PASSWORD_KEY);
        passwordRef.current = null;
        setPassword(null);
        setState((prev) => ({
          ...prev,
          isConnected: false,
          isLoading: false,
          needsPasswordAuth: true,
          error: ERROR_MESSAGES.INCORRECT_PASSWORD,
        }));
        return;
      }

      // On a wallet's routes, 409 means another process holds the wallet open.
      const errorMessage =
        err instanceof SatchelApiException
          ? err.code === "CONNECTION_ERROR"
            ? ERROR_MESSAGES.CONNECTION_ERROR
            : err.status === 409
              ? ERROR_MESSAGES.IN_USE
              : err.message
          : rawMessage || ERROR_MESSAGES.WALLET_LOAD_FAILED;

      // A 404 can be a missing contract as well as a missing wallet; only the
      // server's wallet list says the wallet is gone.
      if (
        err instanceof SatchelApiException &&
        err.status === 404 &&
        !(await validateWalletExists(currentWalletName, currentNetwork))
      ) {
        console.warn(
          `[SatchelWalletContext] Wallet "${currentWalletName}" not found on backend, clearing cache`,
        );
        clearStaleWalletCache();
        return;
      }

      setState((prev) => ({
        ...prev,
        isConnected: err instanceof SatchelApiException && err.code !== "CONNECTION_ERROR",
        isLoading: false,
        error: errorMessage,
      }));
    } finally {
      isRefreshingRef.current = false;
    }
  }, [api, clearStaleWalletCache, validateWalletExists]);

  const sync = useCallback(async () => {
    // Use refs to get current values (avoids stale closure)
    const currentWalletName = walletNameRef.current;
    const currentPassword = passwordRef.current;
    const currentNetwork = networkRef.current;

    if (!currentWalletName || !currentPassword) {
      setState((prev) => ({
        ...prev,
        error: ERROR_MESSAGES.NOT_SET_UP,
      }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await api.syncWallet(currentWalletName, currentPassword, currentNetwork);
      await refresh();
    } catch (err) {
      const errorMessage =
        err instanceof SatchelApiException
          ? err.status === 409
            ? ERROR_MESSAGES.IN_USE
            : err.message
          : err instanceof Error
            ? err.message
            : "Sync failed";

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [api, refresh]);

  const syncRef = useRef(sync);
  syncRef.current = sync;

  // Show what the server already knows at once, then sync: the Bitcoin wallet
  // sees transactions it did not make only when it syncs.
  useEffect(() => {
    if (state.walletName && password) {
      void refresh().then(() => syncRef.current());
    }
  }, [state.walletName, password, refresh]);

  // Single polling interval for the entire app
  useEffect(() => {
    if (!state.walletName || !password) {
      return;
    }

    const intervalId = setInterval(() => {
      syncRef.current();
    }, RGB_INFRA_POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [state.walletName, password]);

  /**
   * Restore wallet access using password.
   * For returning users who need to re-enter their password after session expires.
   * @param pwd - User's wallet password
   */
  const restoreWithPassword = useCallback((pwd: string): void => {
    const currentWalletName = walletNameRef.current;
    const currentNetwork = networkRef.current;
    if (!currentWalletName) {
      setState((prev) => ({ ...prev, error: ERROR_MESSAGES.NOT_SET_UP }));
      return;
    }

    // Store password and clear auth requirement
    sessionStorage.setItem(WALLET_PASSWORD_KEY, pwd);
    passwordRef.current = pwd;
    setPassword(pwd);
    setState((prev) => ({
      ...prev,
      error: null,
      needsPasswordAuth: false,
    }));

    // Re-cache credentials in Electron for companion server (updates connection.jsonc)
    const electronAPI = getElectronAPI();
    if (isElectron && electronAPI?.cacheWalletCredentials) {
      electronAPI.cacheWalletCredentials({
        walletName: currentWalletName,
        password: pwd,
        network: currentNetwork,
      });
    }
  }, []);

  // Set network and trigger refresh
  const setNetwork = useCallback(
    (network: BitcoinNetwork) => {
      networkRef.current = network;
      localStorage.setItem(NETWORK_KEY, network);
      setState((prev) => ({ ...prev, network }));
      // Refresh balance with new network
      if (walletNameRef.current && passwordRef.current) {
        refresh();
      }
    },
    [refresh],
  );

  const value = useMemo(
    () => ({
      ...state,
      setWallet,
      clearWallet,
      lock,
      refresh,
      sync,
      restoreWithPassword,
      setNetwork,
      api,
      walletPassword: password,
    }),
    [
      state,
      setWallet,
      clearWallet,
      lock,
      refresh,
      sync,
      restoreWithPassword,
      setNetwork,
      api,
      password,
    ],
  );

  return <SatchelWalletContext.Provider value={value}>{children}</SatchelWalletContext.Provider>;
};

/**
 * Hook to access the shared wallet context.
 * Must be used within a SatchelWalletProvider.
 */
export const useSatchelWalletContext = (): SatchelWalletContextValue => {
  const context = useContext(SatchelWalletContext);
  if (!context) {
    throw new Error("useSatchelWalletContext must be used within a SatchelWalletProvider");
  }
  return context;
};
